import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';

const HEADER_BG = 'background.paper';

/**
 * 컬럼 폭(px) 상태 + 리사이즈 핸들 드래그
 * @param {number[]} initialWidths
 * @param {number[]} minWidths
 */
export const useColumnWidths = (initialWidths, minWidths) => {
  const [widths, setWidths] = useState(() => [...initialWidths]);
  const dragRef = useRef(null);
  const minRef = useRef(minWidths);
  minRef.current = minWidths;

  // 컬럼 개수가 바뀌면(HMR·기능 추가) track 수를 맞춤 — 부족하면 마지막 셀이 1열(버전)로 감쌈
  const colCount = initialWidths.length;
  if (widths.length !== colCount) {
    setWidths(
      initialWidths.map((def, i) => (i < widths.length ? widths[i] : def)),
    );
  }

  useEffect(() => {
    const onMove = (e) => {
      const drag = dragRef.current;
      if (!drag) return;
      const delta = e.clientX - drag.startX;
      const i = drag.index;
      const min = minRef.current[i] ?? 40;
      const nextW = Math.max(min, drag.startWidths[i] + delta);
      setWidths((prev) => {
        if (prev[i] === nextW) return prev;
        const next = [...prev];
        next[i] = nextW;
        return next;
      });
    };
    const onUp = () => {
      dragRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []);

  const startResize = useCallback((index, clientX) => {
    setWidths((current) => {
      dragRef.current = {
        index,
        startX: clientX,
        startWidths: [...current],
      };
      return current;
    });
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const cancelResize = useCallback(() => {
    dragRef.current = null;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  const setColumnWidth = useCallback((index, width) => {
    setWidths((prev) => {
      if (index < 0 || index >= prev.length) return prev;
      const min = minRef.current[index] ?? 40;
      const nextW = Math.max(min, Math.ceil(width));
      if (prev[index] === nextW) return prev;
      const next = [...prev];
      next[index] = nextW;
      return next;
    });
  }, []);

  const gridTemplateColumns = widths.map((w) => `${Math.max(0, w)}px`).join(' ');

  return { widths, gridTemplateColumns, startResize, cancelResize, setColumnWidth };
};

const ResizeHandle = ({ onMouseDown, onDoubleClick }) => (
  <Box
    onMouseDown={onMouseDown}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
    }}
    onDoubleClick={onDoubleClick}
    sx={{
      position: 'absolute',
      top: 0,
      // borderRight(1px)와 겹치지 않게 열 경계선 위에만 핸들 배치
      right: -2,
      width: 4,
      height: '100%',
      cursor: 'col-resize',
      zIndex: 2,
      '&:hover': { bgcolor: 'primary.main', opacity: 0.25 },
    }}
  />
);

const cellBaseSx = {
  minWidth: 0,
  maxWidth: '100%',
  width: '100%',
  height: '100%',
  boxSizing: 'border-box',
  px: 1,
  overflow: 'hidden',
};

/** 헤더·본문 공통 행 — 각 셀을 1행·N열에 고정해 감싸짐(버전 칸 합침) 방지 */
export const ColumnRow = ({
  gridTemplateColumns,
  children,
  header = false,
  /** false면 sticky 제거 — 스크롤 영역 밖에 헤더를 둘 때 */
  sticky = true,
  /** true면 열 합계만큼만 넓힘. 오른쪽 빈 칸은 행 밖(범위 선택) */
  fitContent = false,
  sx = {},
  ...rest
}) => (
  <Box
    {...rest}
    sx={{
      display: 'grid',
      gridTemplateColumns,
      alignItems: 'center',
      columnGap: 0,
      width: 'max-content',
      minWidth: fitContent ? 'max-content' : '100%',
      px: 0,
      ...(header
        ? {
            py: 0.5,
            minHeight: 28,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: HEADER_BG,
            ...(sticky
              ? {
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                }
              : {
                  position: 'relative',
                  zIndex: 1,
                }),
          }
        : {}),
      ...sx,
    }}
  >
    {React.Children.map(children, (child, index) => {
      if (child == null || child === false) return null;
      return (
        <Box
          key={React.isValidElement(child) && child.key != null ? child.key : index}
          sx={{
            gridColumn: index + 1,
            gridRow: 1,
            minWidth: 0,
            maxWidth: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
          }}
        >
          {child}
        </Box>
      );
    })}
  </Box>
);

export const ColumnHeaderCell = ({
  label,
  resizable = false,
  onResizeStart,
  onAutoFit,
  fitIndex,
  align = 'left',
  isLast = false,
  sortable = false,
  sortActive = false,
  sortDir = 'asc',
  onSort,
}) => (
  <Box
    data-explorer-fit={fitIndex ?? undefined}
    onClick={sortable ? onSort : undefined}
    role={sortable ? 'button' : undefined}
    title={sortable ? '클릭하여 정렬' : undefined}
    sx={{
      ...cellBaseSx,
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      cursor: sortable ? 'pointer' : 'default',
      userSelect: 'none',
      ...(sortable && {
        '&:hover': { bgcolor: 'action.hover' },
      }),
      ...(!isLast && {
        borderRight: '1px solid',
        borderColor: 'divider',
      }),
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.35,
        minWidth: 0,
        width: '100%',
        justifyContent:
          align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: sortActive ? 700 : 600,
          fontSize: '0.75rem',
          color: sortActive ? 'text.primary' : 'text.secondary',
          textAlign: align,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
        }}
      >
        {label}
      </Typography>
      {sortActive && (
        <Box
          component="span"
          aria-hidden
          sx={{
            flexShrink: 0,
            fontSize: '0.55rem',
            lineHeight: 1,
            color: 'text.secondary',
            transform: sortDir === 'asc' ? 'none' : 'scaleY(-1)',
          }}
        >
          ▲
        </Box>
      )}
    </Box>
    {resizable && (
      <ResizeHandle
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onResizeStart?.(e.clientX);
        }}
        onDoubleClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onAutoFit?.();
        }}
      />
    )}
  </Box>
);

export const ColumnDataCell = ({
  children,
  align = 'left',
  sx = {},
  ...rest
}) => (
  <Box
    {...rest}
    sx={{
      ...cellBaseSx,
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      textAlign: align,
      color: 'text.secondary',
      fontSize: '0.8125rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start',
      ...sx,
    }}
  >
    {children}
  </Box>
);
